import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Activity, Home, LogOut, Menu, X, Settings, Eye, Scan, FileCheck, Calendar, MessageCircle, Megaphone, Mail, FolderOpen, Package, Footprints, Building2, Calculator, ExternalLink, Mic } from "lucide-react";
import { ThyroidIcon } from "@/components/thyroid/ThyroidIcon";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const authHeaders = () => {
  const t = localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/* Module registry — same IDs as MainPage ALL_MODULES */
const MODULE_MAP = {
  schedule:   { label: "Töötajate graafik",       icon: Calendar,       route: "/schedule" },
  chat:       { label: "MedChat",                  icon: MessageCircle,  route: "/chat" },
  notices:    { label: "Teadetetahvel",             icon: Megaphone,      route: "/notices" },
  reminders:  { label: "Meeldetuletused",           icon: Mail,           route: "/reminders" },
  retinal:    { label: "Silmapõhja analüüs",        icon: Eye,            route: "/retinal" },
  ulcers:     { label: "Diabeetiline jalahaavand",   icon: Footprints,     route: "/ulcers" },
  consents:   { label: "Patsiendi nõusolekud",       icon: FileCheck,      route: "/consents" },
  thyroid:    { label: "Kilpnäärme Kliinik",         icon: ThyroidIcon,    route: "/thyroid" },
  graves:     { label: "Gravesi orbitopaatia",       icon: Scan,           route: "/graves" },
  supplies:   { label: "MedTarvikud",                icon: Package,        route: "/supplies" },
  medivoice:  { label: "MediVoice",                  icon: Mic,            route: "/medivoice" },
  documents:  { label: "MedDoc",                     icon: FolderOpen,     route: "/documents" },
};

const DEFAULT_ORDER = Object.keys(MODULE_MAP);

export function AppSidebar({ open, onClose, settingsAction, moduleOrder, hiddenModules, mobileOnly }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [localOrder, setLocalOrder] = useState(null);
  const [localHidden, setLocalHidden] = useState(null);

  const userOrgId = user?.org_id || user?.user?.org_id || "";

  /* If props not passed (pages other than MainPage), fetch from API */
  useEffect(() => {
    if (moduleOrder) return; // Props provided, skip fetch
    if (!userOrgId) return;
    const fetchPrefs = async () => {
      try {
        const res = await fetch(`${API}/admin/organizations/${userOrgId}/module-prefs`, { headers: authHeaders() });
        if (res.ok) {
          const data = await res.json();
          if (data.module_order?.length) setLocalOrder(data.module_order);
          if (data.hidden_modules?.length) setLocalHidden(data.hidden_modules);
        }
      } catch { /* prefs optional */ }
    };
    fetchPrefs();
  }, [userOrgId, moduleOrder, open]);

  const order = moduleOrder || localOrder || DEFAULT_ORDER;
  const hidden = new Set(hiddenModules || localHidden || []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleModuleClick = (mod) => {
    if (mod.external) {
      window.open(mod.external, "_blank");
    } else if (mod.route) {
      navigate(mod.route);
    }
  };

  return (
    <>
      {open && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={onClose} />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 ${
          mobileOnly ? "" : "md:translate-x-0"
        } ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-6 border-b border-slate-200">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
              <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-slate-900">MedPortaal</span>
            </div>
            <button className="md:hidden p-1 hover:bg-slate-100 rounded" onClick={onClose}>
              <X className="w-5 h-5" />
            </button>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {/* Home — always first */}
            <button
              onClick={() => navigate("/")}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              data-testid="nav-home"
            >
              <Home className="w-5 h-5" />
              Pealeht
            </button>

            {/* Dynamic module list — follows MainPage order, skips hidden */}
            {order.map(id => {
              const mod = MODULE_MAP[id];
              if (!mod || hidden.has(id)) return null;
              const Icon = mod.icon;
              return (
                <button
                  key={id}
                  onClick={() => handleModuleClick(mod)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                  data-testid={`nav-${id}`}
                >
                  <Icon className="w-5 h-5" />
                  {mod.label}
                </button>
              );
            })}

            {/* Calculators — always after modules */}
            <button
              onClick={() => navigate("/calculators")}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              data-testid="nav-calculators"
            >
              <Calculator className="w-5 h-5" />
              Kalkulaatorid
            </button>

            {settingsAction && (
              <button
                onClick={settingsAction}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                data-testid="nav-settings"
              >
                <Settings className="w-5 h-5" />
                Seaded
              </button>
            )}
            {(user?.email === "verner.fogel@gmail.com" || user?.user?.email === "verner.fogel@gmail.com" || user?.role === "org_admin" || user?.user?.role === "org_admin") && (
              <button
                onClick={() => navigate("/admin")}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                data-testid="nav-admin"
              >
                <Building2 className="w-5 h-5" />
                Keskuste haldus
              </button>
            )}
          </nav>

          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center gap-3 mb-4">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
              ) : (
                <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-slate-600">{user?.name?.charAt(0) || "U"}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Logi välja
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

export function SidebarMenuButton({ onClick }) {
  return (
    <button
      className="md:hidden p-2 hover:bg-slate-100 rounded-lg"
      onClick={onClick}
      data-testid="mobile-menu-btn"
    >
      <Menu className="w-5 h-5" />
    </button>
  );
}
